import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../api';
import BRAND from '../config/brand';
import { agreementApiUrl } from '../config/agreements';
import {
  MEETING_BOOKING_TYPE,
  clearPendingHold,
  readPendingHold,
  savePendingHold,
} from '../config/booking';
import { GOAL_SETTING_MEETING, MEETING_PATH } from '../config/planningOffers';
import {
  getStoredUtmParams,
  trackBookingStarted,
  trackCheckoutStarted,
} from '../lib/analytics';
import { formatConfirmLabel, formatSlotLabel, groupSlotsByDay } from '../lib/bookingSlots';
import PublicSiteNav from './PublicSiteNav';
import './PublicLinksPage.css';
import './MeetingPages.css';

const MEETING = GOAL_SETTING_MEETING;
const NOT_CHARGED = 'You have not been charged.';

function normalizeSlots(rawSlots = []) {
  return rawSlots.map((slot) => {
    const startsAt = slot.startsAt || slot.id;
    return {
      id: slot.id || startsAt,
      startsAt,
      // Group by the visitor's own calendar day, since times are shown in their time zone.
      dayKey: new Date(startsAt).toLocaleDateString('en-CA'),
      label: formatSlotLabel(new Date(startsAt)),
    };
  });
}

function visitorTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ');
  } catch (_error) {
    return '';
  }
}

async function releaseHold(hold) {
  if (!hold?.holdId) return;
  try {
    await api.post('/api/public/booking/holds/release', hold);
  } catch (_error) {
    // The hold expires on its own if this fails.
  }
}

/**
 * Book a Goal-Setting Meeting: pick a time → the time is held → pay with
 * Stripe → sign the meeting agreement (emailed) → confirmation.
 * Nothing is booked or charged until Stripe confirms the payment.
 */
export default function MeetingBookingPage() {
  const location = useLocation();
  const canceled = new URLSearchParams(location.search).get('canceled') === '1';

  const [slots, setSlots] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | unavailable
  const [selectedId, setSelectedId] = useState('');
  const [guest, setGuest] = useState({ fullName: '', email: '', notes: '', consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const dayGroups = useMemo(() => groupSlotsByDay(slots), [slots]);
  const selected = slots.find((slot) => slot.id === selectedId) || null;
  const timeZone = useMemo(visitorTimeZone, []);

  const loadSlots = useCallback(async () => {
    try {
      const response = await api.get('/api/public/booking/slots');
      const payload = response.data || {};
      if (!payload.success || !payload.paymentReady) {
        setStatus('unavailable');
        return;
      }
      setSlots(normalizeSlots(payload.slots || []));
      setStatus('ready');
    } catch (_error) {
      setStatus('unavailable');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Back from a cancelled checkout: give the held time back before listing times.
      if (canceled) {
        const pending = readPendingHold();
        clearPendingHold();
        await releaseHold(pending);
      }
      if (!cancelled) await loadSlots();
    })();
    return () => {
      cancelled = true;
    };
  }, [canceled, loadSlots]);

  const updateGuest = (field, value) => {
    setGuest((prev) => ({ ...prev, [field]: value }));
  };

  const selectSlot = (slotId) => {
    setSelectedId(slotId);
    trackBookingStarted({ offer_id: MEETING.offerId, booking_type: MEETING_BOOKING_TYPE });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selected || submitting) return;
    if (!guest.fullName.trim() || !guest.email.trim() || !guest.consent) {
      setSubmitError('Your name, email, and agreement to the terms below are needed to book.');
      return;
    }
    setSubmitError('');
    setSubmitting(true);

    // Step 1: hold the time so nobody else can take it during checkout.
    let hold;
    try {
      const response = await api.post('/api/public/booking/holds', { startsAt: selected.startsAt });
      hold = response.data.hold;
    } catch (error) {
      const code = error?.response?.data?.code;
      setSubmitError(
        `${error?.response?.data?.error || 'That time could not be held. Please try again.'} ${NOT_CHARGED}`
      );
      setSubmitting(false);
      if (code === 'SLOT_UNAVAILABLE') {
        setSelectedId('');
        loadSlots();
      }
      return;
    }

    // Step 2: start Stripe Checkout for that held time.
    try {
      const response = await fetch(agreementApiUrl('/api/checkout/hourly'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: guest.fullName.trim(),
          email: guest.email.trim(),
          topicNotes: guest.notes.trim(),
          startsAt: hold.startsAt,
          holdId: hold.holdId,
          holdToken: hold.holdToken,
          holdExpiresAt: hold.expiresAt,
          // Campaign labels only; lets a later plan purchase be traced to the platform.
          attribution: getStoredUtmParams(),
          consent: true,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.checkoutUrl) {
        throw new Error(payload.message || '');
      }
      savePendingHold(hold);
      trackCheckoutStarted({
        offer_id: MEETING.offerId,
        booking_type: MEETING_BOOKING_TYPE,
        value: MEETING.price,
      });
      window.location.assign(payload.checkoutUrl);
    } catch (error) {
      // Payment never started: free the time straight away.
      await releaseHold(hold);
      setSubmitError(
        `${error?.message || 'Payment could not be started.'} ${NOT_CHARGED} Please try again.`.trim()
      );
      setSubmitting(false);
      loadSlots();
    }
  };

  return (
    <div className="public-linktree-page public-service-page">
      <PublicSiteNav />
      <main className="public-linktree-shell public-service-shell public-lead-shell">
        <section className="public-service-panel public-lead-card" aria-labelledby="booking-heading">
          <Link className="public-service-logo" to="/links" aria-label="Back to planning links">
            <img src={BRAND.logoSrc} alt="Bautista Planning and Analytics" />
          </Link>
          <p className="public-linktree-kicker">
            {MEETING.name} · {MEETING.durationMinutes} minutes · {MEETING.priceLabel}
          </p>
          <h1 id="booking-heading">Pick a time for your {MEETING.name}</h1>
          <p>
            Choose a time, pay {MEETING.priceLabel}, and you’ll get an email to sign a short
            agreement for this one meeting. <Link to={MEETING_PATH}>What the meeting covers</Link>
          </p>

          {canceled && (
            <div className="public-lead-form-note" role="status">
              Checkout was cancelled. {NOT_CHARGED} The time you picked has been released, so you
              can choose again whenever you’re ready.
            </div>
          )}

          {status === 'loading' && <p>Loading open times…</p>}

          {status === 'unavailable' && (
            <div className="meeting-status meeting-status--attention" role="alert">
              <p>
                Online booking isn’t available right now. Please try again a little later. Nothing
                has been booked or charged.
              </p>
            </div>
          )}

          {status === 'ready' && dayGroups.length === 0 && (
            <p>There are no open times in the next two weeks. Please check back soon.</p>
          )}

          {status === 'ready' && dayGroups.length > 0 && (
            <form className="public-lead-form public-booking-form" onSubmit={handleSubmit}>
              {timeZone && <p className="public-lead-form-note">Times are shown in your time zone ({timeZone}).</p>}
              <div className="public-booking-days">
                {dayGroups.map((group) => (
                  <fieldset key={group.dayKey} className="public-booking-day">
                    <legend>{group.dayLabel}</legend>
                    <div className="public-booking-slots" role="list">
                      {group.slots.map((slot) => {
                        const checked = selectedId === slot.id;
                        return (
                          <label
                            key={slot.id}
                            className={`public-booking-slot${checked ? ' is-selected' : ''}`}
                            role="listitem"
                          >
                            <input
                              type="radio"
                              name="meeting_slot"
                              value={slot.id}
                              checked={checked}
                              onChange={() => selectSlot(slot.id)}
                            />
                            <span>{slot.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
              </div>

              {selected && (
                <p className="public-booking-selection">
                  Selected: <strong>{formatConfirmLabel(selected.startsAt)}</strong>
                </p>
              )}

              <div className="public-lead-grid">
                <label>
                  Full name <span>*</span>
                  <input
                    type="text"
                    autoComplete="name"
                    value={guest.fullName}
                    onChange={(e) => updateGuest('fullName', e.target.value)}
                    required
                  />
                </label>
                <label>
                  Email <span>*</span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={guest.email}
                    onChange={(e) => updateGuest('email', e.target.value)}
                    required
                  />
                </label>
                <label className="public-lead-message">
                  What do you want to cover? (optional)
                  <textarea
                    rows="3"
                    value={guest.notes}
                    onChange={(e) => updateGuest('notes', e.target.value)}
                    placeholder="Questions or decisions you want to talk through"
                  />
                </label>
              </div>

              <div className="meeting-terms">
                <p><strong>Refund.</strong> {MEETING.refundWording}</p>
                <p><strong>Credit.</strong> {MEETING.creditWording}</p>
                <p><strong>Changing your time.</strong> {MEETING.cancellationWording}</p>
                <p>
                  You can read the firm’s{' '}
                  <a href={agreementApiUrl('/api/adv/download')} target="_blank" rel="noopener noreferrer">
                    Form ADV brochure
                  </a>{' '}
                  before you pay.
                </p>
              </div>

              <label className="public-lead-consent">
                <input
                  type="checkbox"
                  checked={guest.consent}
                  onChange={(e) => updateGuest('consent', e.target.checked)}
                  required
                />
                <span>
                  I agree to pay {MEETING.priceLabel} for a {MEETING.durationMinutes}-minute planning
                  session and to receive the agreement for e-signature.
                </span>
              </label>

              {submitError && <div className="public-lead-error" role="alert">{submitError}</div>}

              <div className="public-service-actions">
                <button
                  type="submit"
                  className="public-service-cta public-service-cta--primary"
                  disabled={!selected || submitting}
                >
                  {submitting ? 'Holding your time…' : `Pay ${MEETING.priceLabel} and continue`}
                </button>
              </div>
              <p className="public-lead-form-note">
                Payment is handled by Stripe. Your time is held for you while you check out.
              </p>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}
